import ActionCable from 'actioncable';
import { useEffect, useRef, useState } from 'react';
import { makeGETRequest } from '../../../utils/Axios';

const CHANNEL_NAME = 'UserChannel';
const RECONNECT_INTERVAL_MS = 3000;
const TOKEN_REFRESH_URL = '/api/v1/action_cable/refresh_user_token';

// Subscribes to the Rails UserChannel for a given user and invokes `onUpdate`
// with the payload of every `{ type: 'update' }` broadcast (the user's tracked
// card, or null). Owns the consumer, subscription, and reconnect lifecycle.
//
// The UserChannel connection is authenticated: the Rails
// ApplicationCable::Connection derives current_user from a JWT passed as the
// `token` query param, so we mint a short-lived user channel token before
// creating the consumer and re-mint it on every reconnect (the token can
// expire). Without it the server rejects the subscription and the tracked card
// silently disappears on reload.
//
// Passing a falsy `userId` is a no-op (no subscription), so callers can gate the
// subscription without breaking the rules of hooks. `onUpdate` is read through a
// ref, so it can change between renders without resubscribing.
function useUserChannel({ userId, onUpdate }) {
    const [connected, setConnected] = useState(false);

    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;

    const cableRef = useRef(null);
    const subscriptionRef = useRef(null);
    const reconnectIntervalRef = useRef(null);

    useEffect(() => {
        if (!userId) return undefined;

        let cancelled = false;

        const fetchToken = async () => {
            try {
                const response = await makeGETRequest(TOKEN_REFRESH_URL);
                if (response.data.status !== 'ok') return null;
                return response.data.token;
            } catch (error) {
                return null;
            }
        };

        const teardownConsumer = () => {
            subscriptionRef.current?.unsubscribe();
            if (cableRef.current && subscriptionRef.current) {
                cableRef.current.subscriptions.remove(subscriptionRef.current);
            }
            subscriptionRef.current = null;
            cableRef.current?.disconnect();
            cableRef.current = null;
        };

        const handleReceiveData = (response) => {
            const { data, type } = response.data;
            if (type === 'update') {
                onUpdateRef.current?.(data);
            }
        };

        const handleReconnect = () => {
            clearInterval(reconnectIntervalRef.current);
            reconnectIntervalRef.current = setInterval(() => {
                if (cancelled) return;
                // Rebuild the consumer with a fresh token: a rejected/expired
                // token can't be recovered by reopening the same connection.
                connect();
            }, RECONNECT_INTERVAL_MS);
        };

        const connect = async () => {
            const token = await fetchToken();
            if (cancelled) return;

            if (!token) {
                handleReconnect();
                return;
            }

            teardownConsumer();

            const cable = ActionCable.createConsumer(
                `${process.env.REACT_APP_CABLE_URL}?token=${token}`
            );
            cableRef.current = cable;

            subscriptionRef.current = cable.subscriptions.create(
                { channel: CHANNEL_NAME, user_id: userId },
                {
                    connected: () => {
                        setConnected(true);
                        clearInterval(reconnectIntervalRef.current);
                    },
                    disconnected: () => {
                        setConnected(false);
                        handleReconnect();
                    },
                    received: handleReceiveData,
                }
            );
        };

        connect();

        return () => {
            cancelled = true;
            clearInterval(reconnectIntervalRef.current);
            teardownConsumer();
        };
    }, [userId]);

    return { connected };
}

export default useUserChannel;
